import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTelegram } from '@/hooks/useTelegram';
import { useAuth } from '@/hooks/useAuth';
import { api } from '@/services/api';

interface GameSession {
  id: string;
  name: string | null;
  currentScene: string | null;
  status: string;
}

interface SessionCheckResponse {
  session: GameSession;
  messages: unknown[];
}

export function AdventureStartPage() {
  const navigate = useNavigate();
  const { hapticFeedback } = useTelegram();
  const { character } = useAuth();

  const [hasActiveSession, setHasActiveSession] = useState(false);
  const [hasSavedSessions, setHasSavedSessions] = useState(false);
  const [activeSession, setActiveSession] = useState<GameSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isStarting, setIsStarting] = useState(false);

  useEffect(() => {
    checkSessions();
  }, []);

  const checkSessions = async () => {
    try {
      setIsLoading(true);

      // Check for active session
      const activeResponse = await api.get<SessionCheckResponse | null>('/game/session');
      if (activeResponse && activeResponse.session) {
        setHasActiveSession(true);
        setActiveSession(activeResponse.session);
      } else {
        setHasActiveSession(false);
        setActiveSession(null);
      }

      // Check for saved sessions (to show/hide "Load game" button)
      const sessionsResponse = await api.get<GameSession[]>('/game/sessions');
      if (sessionsResponse && sessionsResponse.length > 0) {
        setHasSavedSessions(true);
      } else {
        setHasSavedSessions(false);
      }
    } catch (err) {
      console.error('Failed to check sessions:', err);
      setHasActiveSession(false);
      setHasSavedSessions(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinue = () => {
    hapticFeedback('medium');
    navigate('/adventure/play');
  };

  const handleLoadGame = () => {
    hapticFeedback('light');
    navigate('/adventure/sessions');
  };

  const handleNewGame = async () => {
    hapticFeedback('medium');
    setIsStarting(true);

    try {
      await api.post('/game/session/new', {});
      navigate('/adventure/play');
    } catch (err) {
      console.error('Failed to start new game:', err);
      hapticFeedback('error');
      setIsStarting(false);
    }
  };

  if (!character?.characterClass) {
    return (
      <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center p-6 safe-area-top safe-area-bottom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <span className="text-6xl block mb-4">⚔️</span>
          <h1 className="text-2xl font-bold text-white mb-2">Нет персонажа</h1>
          <p className="text-gray-400 mb-6">Создайте персонажа чтобы начать приключение</p>
          <button
            onClick={() => navigate('/character-creation')}
            className="px-6 py-3 bg-amber-500 text-black rounded-xl font-bold"
          >
            Создать персонажа
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col safe-area-top safe-area-bottom">
      {/* Header */}
      <div className="p-4 flex items-center">
        <button
          onClick={() => {
            hapticFeedback('light');
            navigate('/');
          }}
          className="text-gray-400 hover:text-white transition-colors"
        >
          ← Назад
        </button>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <span className="text-6xl block mb-4">⚔️</span>
          <h1 className="text-3xl font-bold text-white mb-2">Приключение</h1>
          <p className="text-gray-400">
            {character.name} • {character.characterClass}
          </p>
        </motion.div>

        {/* Menu buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="w-full max-w-xs space-y-4"
        >
          {/* Continue button - only show if active session */}
          {!isLoading && hasActiveSession && (
            <motion.button
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              onClick={handleContinue}
              className="w-full py-4 px-6 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-black font-bold text-lg shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 transition-shadow flex items-center justify-center gap-3"
            >
              <span className="text-2xl">▶️</span>
              <span>Продолжить</span>
            </motion.button>
          )}

          {/* Load game button - only show if there are saved sessions */}
          {!isLoading && hasSavedSessions && (
            <button
              onClick={handleLoadGame}
              className="w-full py-4 px-6 rounded-xl bg-gray-800 border border-gray-700 text-white font-bold text-lg hover:bg-gray-750 transition-colors flex items-center justify-center gap-3"
            >
              <span className="text-2xl">📂</span>
              <span>Загрузить игру</span>
            </button>
          )}

          {/* New game button */}
          <button
            onClick={handleNewGame}
            disabled={isStarting}
            className="w-full py-4 px-6 rounded-xl bg-gray-800 border border-gray-700 text-white font-bold text-lg hover:bg-gray-750 transition-colors flex items-center justify-center gap-3 disabled:opacity-50"
          >
            <span className="text-2xl">✨</span>
            <span>{isStarting ? 'Создание...' : 'Новая игра'}</span>
          </button>
        </motion.div>

        {/* Active session info */}
        {!isLoading && activeSession && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-8 p-4 rounded-xl bg-gray-800/50 border border-gray-700/50 max-w-xs w-full"
          >
            <p className="text-sm text-gray-400 mb-1">Текущая сессия:</p>
            <p className="text-white font-medium truncate">
              {activeSession.name || 'Безымянное приключение'}
            </p>
            {activeSession.currentScene && (
              <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                {activeSession.currentScene}
              </p>
            )}
          </motion.div>
        )}
      </div>

      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-gray-900 flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full" />
        </div>
      )}
    </div>
  );
}
